import { FC } from "react";
import { observer } from "mobx-react";
import { Box, Typography } from "@mui/material";

import Column from "../core/Column";
import Row from "../core/Row";
import { useAuth } from "../../../contexts/AuthContext";
import { useCapabilities } from "../../../contexts/permissions/useCapabilities";
import { ExtendedHomeStatisticsSection } from "./components/ExtendedHomeStatisticsSection";
import { ExtendedHomeClaimsSection } from "./components/ExtendedHomeClaimsSection";
import { ExtendedHomeAskReveSection } from "./components/ExtendedHomeAskReveSection";
import { ExtendedHomeQuickActionsSection } from "./components/ExtendedHomeQuickActionsSection";

const ExtendedHomePage: FC = observer(() => {
  const { user } = useAuth();
  // Statistics, claims data and filter options are all gated server-side
  // behind showClaims (and /claims isn't routed without it), so a showHome-only
  // session would just get erroring cards and a dead "View all claims" link.
  const { showClaims } = useCapabilities();
  const displayName =
    user?.backend?.first_name ||
    user?.backend?.given_name ||
    user?.backend?.name?.split(" ")[0] ||
    user?.backend?.username ||
    user?.username;

  return (
    <main className="grow flex flex-col bg-hex-gray-100 h-[calc(100%-6rem)] overflow-y-auto overflow-x-hidden">
      <Column
        sx={{
          p: 3,
          gap: 2,
          height: "auto",
          containerType: "inline-size",
          containerName: "home-content",
        }}
      >
        <Typography
          variant="h3"
          sx={{ color: "text.primary", fontWeight: 700 }}
        >
          Welcome back{displayName ? `, ${displayName}` : ""}
        </Typography>

        {showClaims ? <ExtendedHomeStatisticsSection /> : null}

        <Row
          sx={{
            flexDirection: "column",
            alignItems: "stretch",
            gap: 3,
            height: "auto",
            "@container home-content (min-width: 700px)": {
              flexDirection: "row",
            },
          }}
        >
          {/* Your Claims */}
          {showClaims ? (
            <Box
              sx={{
                flex: "1 1 auto",
                width: "auto",
                minWidth: 0,
                "@container home-content (min-width: 700px)": {
                  flex: "2 1 0",
                },
              }}
            >
              <ExtendedHomeClaimsSection />
            </Box>
          ) : null}

          {/* Ask Reve + Quick actions */}
          <Column
            sx={{
              flex: "1 1 auto",
              width: "auto",
              height: "auto",
              minWidth: 0,
              gap: 2,
              "@container home-content (min-width: 700px)": {
                flex: "1 1 0",
              },
            }}
          >
            <ExtendedHomeAskReveSection />
            <ExtendedHomeQuickActionsSection />
          </Column>
        </Row>
      </Column>
    </main>
  );
});

export default ExtendedHomePage;
